import { useEntities, useEntity } from "@ui/hooks/serviceUtils";
import useDataManager, { DataManagerProps } from "@ui/hooks/useDataManager";
import { ISupplier } from "./useSupplier";
import { DateTime } from "luxon";
import { IPartialDocument } from "@ui/hooks/defs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import axios from "axios";
import { useToast } from "@chakra-ui/react";

export interface ISupplierBooking extends IPartialDocument {
  id: string;
  supplier: ISupplier;
  date: DateTime; // UTC time string
  meal: string;
  transactionId?: string;
  selectedDays?: string[];
}

export interface ISupplierBookingDto {
  id: string;
  name: string;
  meal: string;
  startdate: DateTime;
  enddate: DateTime;
}

const key = "supplierBooking";
const endpoint = "/api/supplierbooking";

export const useSupplierBookingsQuery = (
  props: DataManagerProps<ISupplierBookingDto> = {},
) => useDataManager<ISupplierBookingDto>(key, `${endpoint}/query`, props);
export const useSupplierBookings = () =>
  useEntities<ISupplierBookingDto>(key, endpoint);
export const useSupplierBooking = (
  id: string | undefined,
  toast: boolean = true,
) => {
  const base = useEntity<ISupplierBooking>(
    key,
    endpoint,
    id,
    toast
      ? {
          add: "Supplier Booking added",
          update: "Supplier Booking updated",
          delete: "Supplier Booking deleted",
        }
      : undefined,
  );

  return { ...base };
};

export const getTodaySuppliers = () => {
  const today = useMemo(() => DateTime.now(), []);
  const dateString = today.toISODate();

  return useQuery<any, Error>({
    queryKey: [key, "date"],
    queryFn: async () => {
      const url = `${endpoint}/today?date=${dateString}`;
      const { data } = await axios.get<any>(url);

      return data;
    },
  });
};
export interface ISupplierBookingMenu extends IPartialDocument {
  meal: string;
  startDate: DateTime;
  endDate?: DateTime;
  selectedDays?: string[];
}

export const useUpdateSupplierMenu = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (data: ISupplierBookingMenu) => {
      const url = `${endpoint}/add-menu`;
      const { data: response } = await axios.post(url, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [key] });

      toast({
        title: "Supplier menu is updated.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    },
    onError: (error: any) => {
      toast({
        title: error?.response?.data || error.message || "An error occurred.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    },
  });
};

export const useDisplaySupplierMenu = (date: DateTime | null | undefined) => {
  return useQuery<any, Error>({
    queryKey: [key, "menu", date],
    queryFn: async () => {
      const formattedDate =
        date instanceof DateTime
          ? date.toISODate()
          : date
            ? DateTime.fromJSDate(date).toISODate()
            : null;
      const url = `${endpoint}/menu?date=${formattedDate}`;
      const { data } = await axios.get(url);
      return data;
    },
    enabled: !!date,
  });
};

export const getmenu = (startDate: DateTime, endDate: DateTime) => {
  return useQuery<any, Error>({
    queryKey: [key, "daterange", startDate.toISODate(), endDate.toISODate()],
    queryFn: async () => {
      const formattedStartDate =
        startDate instanceof DateTime
          ? startDate.toISODate()
          : startDate
            ? DateTime.fromJSDate(startDate).toISODate()
            : null;

      const formattedEndDate =
        endDate instanceof DateTime
          ? endDate.toISODate()
          : endDate
            ? DateTime.fromJSDate(endDate).toISODate()
            : null;

      const url = `${endpoint}/menus?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
      const { data } = await axios.get(url);
      return data;
    },
  });
};
